import { useCallback } from 'react';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import {
  createSchedule as apiCreateSchedule,
  updateSchedule as apiUpdateSchedule,
  deleteSchedule as apiDeleteSchedule,
  getScheduleById,
} from '../../api/schedule';
import { ScheduleInfo, CreateScheduleParams, UpdateScheduleParams } from '../../types/schedule.types';
import type { ScheduleDataCache } from '../../types/cache.types';

export interface UseScheduleUpdatesOptions {
  onSuccess?: (action: 'create' | 'update' | 'delete', data: unknown) => void;
  onError?: (error: Error, action: string, data: unknown) => void;
}

/**
 * Schedule 聚合（按分场的日程列表缓存）创建 / 更新 / 删除。
 */
export function useScheduleUpdates(options?: UseScheduleUpdatesOptions) {
  const { getScheduleCache, setScheduleCache, getSlotCache, setSlotCache } = useDataCache();

  const createSchedule = useCallback(
    async (breakdownId: string, data: CreateScheduleParams): Promise<string> => {
      try {
        const response = await apiCreateSchedule(data);
        const newScheduleId = response.data?.scheduleID;

        if (!newScheduleId) {
          throw new Error('创建日程失败：未返回 scheduleID');
        }

        const cache = getScheduleCache(breakdownId);
        if (cache) {
          const newSchedule = {
            ...data,
            scheduleID: newScheduleId,
          } as ScheduleInfo;

          setScheduleCache(breakdownId, {
            schedules: [...cache.schedules, newSchedule],
          });
        }

        if (options?.onSuccess) {
          options.onSuccess('create', { scheduleId: newScheduleId, data });
        }

        return newScheduleId;
      } catch (error) {
        console.error('[useScheduleUpdates] createSchedule:', error);

        if (options?.onError) {
          options.onError(error as Error, 'create', { data });
        }

        throw error;
      }
    },
    [getScheduleCache, setScheduleCache, options]
  );

  const patchSchedule = useCallback(
    async (scheduleId: string, updates: UpdateScheduleParams) => {
      let oldCache: ScheduleDataCache | null = null;
      let oldSchedules: ScheduleInfo[] = [];
      let oldSlotScheduleInfo: ScheduleInfo | null = null;
      let breakdownId: string | undefined;

      try {
        const scheduleInfoResponse = await getScheduleById(scheduleId);
        const scheduleInfo = scheduleInfoResponse.data;
        breakdownId = scheduleInfo?.scheduleBelongBreakdownID;

        if (!breakdownId) {
          throw new Error('无法获取 breakdownId，无法更新缓存');
        }

        const cache = getScheduleCache(breakdownId);
        if (cache) {
          oldCache = { ...cache };
          oldSchedules = [...cache.schedules];

          const updatedSchedules = cache.schedules.map((row) =>
            row.scheduleID === scheduleId ? { ...row, ...updates } : row
          );

          const nextPatch: Partial<ScheduleDataCache> = {
            schedules: updatedSchedules,
          };
          if (
            cache.currentScheduleId === scheduleId &&
            cache.currentScheduleInfo &&
            cache.currentScheduleInfo.scheduleID === scheduleId
          ) {
            nextPatch.currentScheduleInfo = { ...cache.currentScheduleInfo, ...updates };
          }

          setScheduleCache(breakdownId, nextPatch);
        }

        const slotCache = getSlotCache(scheduleId);
        if (slotCache?.scheduleInfo) {
          oldSlotScheduleInfo = slotCache.scheduleInfo;
          setSlotCache(scheduleId, {
            scheduleInfo: { ...slotCache.scheduleInfo, ...updates },
          });
        }

        await apiUpdateSchedule(scheduleId, updates);

        if (options?.onSuccess) {
          options.onSuccess('update', { scheduleId, updates });
        }
      } catch (error) {
        console.error('[useScheduleUpdates] patchSchedule:', error);

        if (breakdownId && oldCache) {
          setScheduleCache(breakdownId, {
            schedules: oldSchedules,
            currentScheduleInfo: oldCache.currentScheduleInfo,
          });
        }
        if (oldSlotScheduleInfo) {
          setSlotCache(scheduleId, { scheduleInfo: oldSlotScheduleInfo });
        }

        if (options?.onError) {
          options.onError(error as Error, 'update', { scheduleId, updates });
        }

        throw error;
      }
    },
    [getScheduleCache, setScheduleCache, getSlotCache, setSlotCache, options]
  );

  const removeSchedule = useCallback(
    async (scheduleId: string) => {
      let oldCache: ScheduleDataCache | null = null;
      let oldSchedules: ScheduleInfo[] = [];
      let breakdownId: string | undefined;

      try {
        const scheduleInfoResponse = await getScheduleById(scheduleId);
        const scheduleInfo = scheduleInfoResponse.data;
        breakdownId = scheduleInfo?.scheduleBelongBreakdownID;

        if (!breakdownId) {
          throw new Error('无法获取 breakdownId，无法更新缓存');
        }

        const cache = getScheduleCache(breakdownId);
        if (cache) {
          oldCache = { ...cache };
          oldSchedules = [...cache.schedules];

          const updatedSchedules = cache.schedules.filter((row) => row.scheduleID !== scheduleId);

          setScheduleCache(breakdownId, {
            schedules: updatedSchedules,
          });
        }

        await apiDeleteSchedule(scheduleId);

        if (options?.onSuccess) {
          options.onSuccess('delete', { scheduleId });
        }
      } catch (error) {
        console.error('[useScheduleUpdates] removeSchedule:', error);

        if (breakdownId && oldCache) {
          setScheduleCache(breakdownId, {
            schedules: oldSchedules,
          });
        }

        if (options?.onError) {
          options.onError(error as Error, 'delete', { scheduleId });
        }

        throw error;
      }
    },
    [getScheduleCache, setScheduleCache, options]
  );

  return {
    createSchedule,
    patchSchedule,
    removeSchedule,
  };
}
